//#region ImportsSection
import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  Button,
  Row,
} from "reactstrap";
import { GetLanguageString } from "../../../helper/Components";
import {
  convertDataFromSnakeCaseToCamelCase,
  LoadingSpinner,
} from "../../../../constants/const";
import Draggable from "react-draggable";
import swal from "sweetalert";
import { t } from "i18next";
import { ApiFinance } from "../../ApiFinance";
import { encryptStorage } from "../../../../constants/EncryptStorage";
import { GetCustomStyles } from "../../../helper/Method";
import CashToAmanatForm from "./CashToAmanatForm";
import { validateTransactionDate } from "../../fiscalYearHelper";
//#endregion ImportsSection
const EditCashToAmanatTransaction = ({ setModal, transNo, onSuccess }) => {
  const [disablesubmitButton, setDisableSubmitButton] = useState(true);
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));
  const [fileChanged, setFileChanged] = useState(false);
  const [initialDate, setInitialDate] = useState(null);

  const [values, setValues] = useState({
    transferAmount: null,
    bankCharge: null,
    memo: "",
    referenceNo: null,
    depositDate: new Date(),
    file: null,
    isFileOnly: false,
  });
  const [errors, setErrors] = useState({});

  const toggle = () => setModal(false);
  //#region StateSection
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(false);
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [fundAccountBalances, setFundAccountBalances] = useState([]);
  //#endregion StateSection
  //#region useEffectSection
  useEffect(() => {
    setLoading(true);
    ApiFinance()
      .GetCashToAmanatTransferData(loggedInUnitId)
      .then((res) => setFundAccountBalances(res.data))
      .catch((err) =>
        console.error("Error fetching fund account balances:", err)
      )
      .finally(() => {
        setLoading(false);
      });
  }, [loggedInUnitId]);
  useEffect(() => {
    if (transNo) {
      setLoading(true);
      ApiFinance()
        .GetTransactionDetails(transNo)
        .then((res) => {
          // Handle response data here
          var convertedData = convertDataFromSnakeCaseToCamelCase(res.data);
          setTransactionDetails(convertedData);
        })
        .catch((error) => {
          console.error("Error fetching transaction details:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [transNo]);

  useEffect(() => {
    if (transactionDetails) {
      const parsedDate = parseDDMMYYYY(transactionDetails.date);
      setValues({
        transferAmount: transactionDetails.amount || "",
        bankCharge: transactionDetails.bankCharge || "",
        depositDate: parsedDate,

        referenceNo: transactionDetails.ref,
        memo: transactionDetails.memo || "",
        file: null, // file should always be blank initially
      });
      setInitialDate(parsedDate);
      setFileChanged(false);
      setDisableSubmitButton(true);
    }
  }, [transactionDetails]);

  function parseDDMMYYYY(dateStr) {
    if (!dateStr) return null;
    const [day, month, year] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  //#endregion useEffectSection

  //#region functionSection
  const handleDrag = (e, ui) => {
    const { x, y } = position;
    setPosition({ x: x + ui.deltaX, y: y + ui.deltaY });
  };

  // INPUT HANDLER
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDisableSubmitButton(false);
    let newValue = value;

    // Enforce numeric-only inputs
    if (name === "transferAmount" || name === "bankCharge") {
      newValue = value.replace(/\D/g, "");
      e.target.value = newValue;
    }

    // Always update values first
    setValues((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Clear previous error for this field
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    // Now apply validation only for transferAmount
    if (name === "transferAmount") {
      const transferAmount = Number(newValue) || 0;
      const cashBalance = Number(fundAccountBalances.cashAccountBalance) || 0;

      if (transferAmount > cashBalance) {
        setErrors((prev) => ({
          ...prev,
          transferAmount: t(
            "transfer_amount_greater_than_cash_account_balance"
          ),
        }));

        setValues((prev) => ({
          ...prev,
          file: null,
        }));
      }
    }
  };

  // DATE HANDLER
  const handleDateChange = (date) => {
    setDisableSubmitButton(false);
    setValues({
      ...values,
      depositDate: date,
    });
    setErrors((prev) => ({ ...prev, depositDate: "" }));
  };

  // FILE HANDLER
  const handleFileChange = (e) => {
    setDisableSubmitButton(false);
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    const maxSize = 1 * 1024 * 1024;
    if (uploadedFile.size > maxSize) {
      setErrors({ ...errors, file: "File must be under 1MB" });
      return;
    } else {
      setErrors((prev) => ({ ...prev, file: "" }));
    }
    setFileChanged(true);
    setValues((prev) => ({ ...prev, file: uploadedFile, isFileOnly: true }));

    setDisableSubmitButton(false);
  };

  const validate = () => {
    let temp = {};

    temp.transferAmount = "";

    const transferAmount = Number(values.transferAmount || 0);
    const cashBalance = Number(fundAccountBalances.cashAccountBalance || 0);

    // 1. Required
    if (!values.transferAmount) {
      temp.transferAmount = t("common_field_required_error");

      // 2. Not zero
    } else if (transferAmount === 0) {
      temp.transferAmount = t("common_field_required_error");

      // 3. Greater than cash balance
    } else if (transferAmount > cashBalance) {
      temp.transferAmount = t(
        "transfer_amount_greater_than_cash_account_balance"
      );
    }
    temp.bankCharge = values.bankCharge ? "" : "";
    const dateError = validateTransactionDate(values.depositDate, initialDate);
    if (dateError) {
      temp.depositDate = t(dateError);
    } else {
      temp.depositDate = values.depositDate
        ? ""
        : t("common_field_required_error");
    }
    temp.referenceNo = values.referenceNo
      ? ""
      : t("common_field_required_error");

    temp.memo = values.memo && values.memo.trim() !== "" ? "" : t("common_field_required_error");

    setErrors({ ...temp });

    return Object.values(temp).every((x) => x === "");
  };
  const formatDate = (dateObj) => {
    if (!dateObj) return "";
    const yyyy = dateObj.getFullYear();
    const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
    const dd = String(dateObj.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const parseDDMMYYYYTN = (dateStr) => {
    if (!dateStr) return null;
    const [dd, mm, yyyy] = dateStr.split("-").map(Number);
    return new Date(yyyy, mm - 1, dd);
  };

  const isDataChanged = () => {
    if (!transactionDetails) return false;
    const currentMemo = (values.memo || "").trim();
    const originalMemo = (transactionDetails.memo || "").trim();
    const memoChanged = currentMemo !== originalMemo;

    const currentAmount = Number(values.transferAmount || 0);
    const originalAmount = Number(transactionDetails.amount || 0);
    const amountChanged = currentAmount !== originalAmount;

    const ref = transactionDetails.ref || "";
    const currentRefNo = (values.referenceNo || "").trim();
    const refChanged = currentRefNo !== ref;

    const originalDateObj = parseDDMMYYYYTN(transactionDetails.date);
    const currentDateStr = formatDate(values.depositDate);
    const originalDateStr = formatDate(originalDateObj);
    const dateChanged = currentDateStr !== originalDateStr;

    return memoChanged || amountChanged || refChanged || dateChanged;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const dataChanged = isDataChanged();
    const fileChangedLocal = fileChanged;

    if (!dataChanged && !fileChangedLocal) {
      swal({
        title: "No changes detected.",
        icon: "info",
        buttons: "OK",
      });
      return;
    }

    // If only file changed, skip field validation
    if (!fileChangedLocal && !validate()) return;

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("TransactionNo", transNo);
      formData.append("unitId", loggedInUnitId);

      // Build FormData for three cases:
      // 1) only file changed -> send file + transactionNo + isFileOnly=true
      // 2) only data changed -> send data + explicit empty file field + isFileOnly=false
      // 3) both changed -> send data + file + isFileOnly=false
      if (fileChangedLocal && !dataChanged) {
        formData.append("file", values.file);
        formData.append("isFileOnly", "true");
      } else if (dataChanged && !fileChangedLocal) {
        formData.append("transferAmount", values.transferAmount);
        formData.append("bankCharge", values.bankCharge || 0);
        formData.append("memo", values.memo);
        formData.append("referenceNo", values.referenceNo);
        formData.append("depositDate", formatDate(values.depositDate));
        // some backends expect an explicit empty file field for "no file"
        formData.append("file", "");
        formData.append("isFileOnly", "false");
      } else if (dataChanged && fileChangedLocal) {
        formData.append("transferAmount", values.transferAmount);
        formData.append("bankCharge", values.bankCharge || 0);
        formData.append("memo", values.memo);
        formData.append("referenceNo", values.referenceNo);
        formData.append("depositDate", formatDate(values.depositDate));
        formData.append("file", values.file);
        formData.append("isFileOnly", "false");
      }

      const res = await ApiFinance().EditCashinHandtoAmanat(formData);
      if (res.data.statusCode === "200") {
        swal({
          title: t(res.data.message) || "Success",
          icon: "success",
          buttons: "OK",
        });
        setModal(false);
        try {
          if (typeof onSuccess === "function") onSuccess();
        } catch (e) {
          console.error("onSuccess callback error:", e);
        }
      } else {
        const isWarning = res.data?.statusCode === "300" || res.data?.message === "modification_request_already_pending";
        swal({
          title: t(res.data?.message) || "Error transferring fund",
          icon: isWarning ? "warning" : "error",
          buttons: "OK",
        }).then(() => {
          if (isWarning) {
            setModal(false);
          }
        });
      }
    } catch (error) {
      swal({
        title: t(error?.message) || "Error transferring fund",
        icon: "error",
        buttons: "OK",
      });
    } finally {
      setLoading(false);
      // reset file state properly through setState
      setValues((prev) => ({ ...prev, file: null, isFileOnly: false }));
      setFileChanged(false);
    }
  };

  //#endregion functionSection
  return (
    <Draggable position={position} onDrag={handleDrag} handle=".modal-header" cancel=".no-drag">
      <Modal isOpen className="inmodal" size="lg" autoFocus={false}>
        <ModalHeader className="modal-title" tag="h4">
          {<GetLanguageString props="edit_transaction" />}
        </ModalHeader>

        {loading ? (
          <ModalBody>
            <div className="text-center" style={{ width: "100%" }}>
              <LoadingSpinner />
            </div>
          </ModalBody>
        ) : (
          <Form onSubmit={handleSubmit}>
            <ModalBody>
              {transactionDetails && (
                <CashToAmanatForm
                  values={values}
                  errors={errors}
                  fundAccountBalances={fundAccountBalances}
                  handleInputChange={handleInputChange}
                  handleDateChange={handleDateChange}
                  handleFileChange={handleFileChange}
                  isEdit={true}
                  initialDate={initialDate}
                />
              )}
            </ModalBody>

            <ModalFooter>
              <Row
                style={{
                  backgroundColor: "white",
                  marginLeft: 2,
                  marginRight: 2,
                  height: 50,
                  width: "100%",
                }}
              >
                <div
                  style={{
                    marginTop: 10,
                    display: "flex",
                    gap: 10,
                    justifyContent: "flex-end",
                    width: "100%",
                    alignItems: "center",
                  }}
                >
                  <Button
                    color="white"
                    className={GetCustomStyles().btn_style}
                    onClick={toggle}
                    style={{ marginLeft: 10 }}
                  >
                    <GetLanguageString props="common_cancel" />
                  </Button>
                  <Button
                    color="primary"
                    className={GetCustomStyles().btn_style}
                    disabled={disablesubmitButton}
                    type="submit"
                  >
                    <GetLanguageString props="common_save_changes" />
                  </Button>
                </div>
              </Row>
            </ModalFooter>
          </Form>
        )}
      </Modal>
    </Draggable>
  );
};

export default EditCashToAmanatTransaction;
